// Friend Requests Web Component
import { escapeHtml } from '../utils/html-utils.js';

class FriendRequests extends HTMLElement {
  constructor() {
    super();
    this.receivedRequests = [];
    this.sentRequests = [];
    this.pendingInvitations = [];
    this.isLoading = true;
    this.currentUser = null;
    this.documentListenersAttached = false;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.attachDocumentListeners();
    this.loadRequests();
  }

  render() {
    this.innerHTML = `
      <div class="friend-requests">
        <div class="requests-header">
          <h3>👋 Friend Requests</h3>
          <button class="btn btn-sm btn-outline refresh-btn" id="refreshBtn">
            🔄 Refresh
          </button>
        </div>

        <div class="requests-content">
          ${this.renderContent()}
        </div>
      </div>
    `;
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading friend requests...</p>
        </div>
      `;
    }

    const hasReceivedRequests = this.receivedRequests.length > 0;
    const hasSentRequests = this.sentRequests.length > 0;
    const hasPendingInvitations = this.pendingInvitations.length > 0;

    if (!hasReceivedRequests && !hasSentRequests && !hasPendingInvitations) {
      return `
        <div class="empty-state">
          <span class="empty-state-icon">🤝</span>
          <h4>No friend requests</h4>
          <p>When people send you friend requests, they'll appear here!</p>
        </div>
      `;
    }

    return `
      ${hasPendingInvitations ? `
        <div class="requests-section">
          <div class="section-title">
            📧 Pending Invitations
            <span class="section-count">${this.pendingInvitations.length}</span>
          </div>
          ${this.pendingInvitations.map(invitation => this.renderPendingInvitation(invitation)).join('')}
        </div>
      ` : ''}

      ${hasReceivedRequests ? `
        <div class="requests-section">
          <div class="section-title">
            📥 Received Requests
            <span class="section-count">${this.receivedRequests.length}</span>
          </div>
          ${this.receivedRequests.map(request => this.renderReceivedRequest(request)).join('')}
        </div>
      ` : ''}

      ${hasSentRequests ? `
        <div class="requests-section">
          <div class="section-title">
            📤 Sent Requests
            <span class="section-count">${this.sentRequests.length}</span>
          </div>
          ${this.sentRequests.map(request => this.renderSentRequest(request)).join('')}
        </div>
      ` : ''}
    `;
  }

  renderReceivedRequest(request) {
    return `
      <div class="request-item" data-request-id="${request.request_id}">
        <div class="request-info">
          <div class="avatar">
            ${request.username.charAt(0).toUpperCase()}
          </div>
          <div class="request-details">
            <h4>${escapeHtml(request.username)}</h4>
            <p>${escapeHtml(request.email)}</p>
            <div class="request-date">${this.formatDate(request.request_date)}</div>
          </div>
        </div>
        <div class="request-actions">
          <button 
            class="action-btn accept-btn" 
            data-request-id="${request.request_id}"
            data-action="accept"
          >
            <span>✅</span>
            Accept
          </button>
          <button 
            class="action-btn decline-btn" 
            data-request-id="${request.request_id}"
            data-action="decline"
          >
            <span>❌</span>
            Decline
          </button>
        </div>
      </div>
    `;
  }

  renderSentRequest(request) {
    return `
      <div class="request-item" data-request-id="${request.request_id}">
        <div class="request-info">
          <div class="avatar">
            ${request.username.charAt(0).toUpperCase()}
          </div>
          <div class="request-details">
            <h4>${escapeHtml(request.username)}</h4>
            <p>Request pending...</p>
            <div class="request-date">Sent ${this.formatDate(request.request_date)}</div>
          </div>
        </div>
        <div class="request-actions">
          <span style="color: var(--text-muted); font-size: 0.875rem;">⏳ Waiting for response</span>
        </div>
      </div>
    `;
  }

  renderPendingInvitation(invitation) {
    return `
      <div class="request-item" data-invitation-id="${invitation.invitation_id}">
        <div class="request-info">
          <div class="avatar">
            ${invitation.inviter_username.charAt(0).toUpperCase()}
          </div>
          <div class="request-details">
            <h4>${escapeHtml(invitation.inviter_username)}</h4>
            <p>wants to be friends</p>
            <div class="request-date">Requested ${this.formatDate(invitation.invitation_date)}</div>
          </div>
        </div>
        <div class="request-actions">
          <button 
            class="action-btn accept-btn" 
            data-invitation-id="${invitation.invitation_id}"
            data-action="accept-invitation"
          >
            <span>✅</span>
            Accept
          </button>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    // Refresh button
    const refreshBtn = this.querySelector('#refreshBtn');
    refreshBtn?.addEventListener('click', () => {
      this.loadRequests();
    });

    // Action buttons - use event delegation on the component itself
    // Note: We don't re-add this listener multiple times because
    // we only add it once per component instance
  }

  attachDocumentListeners() {
    // Only attach document-level listeners once
    if (this.documentListenersAttached) {
      return;
    }
    this.documentListenersAttached = true;

    // Action buttons - use event delegation
    this.addEventListener('click', (e) => {
      if (e.target.closest('.action-btn')) {
        const btn = e.target.closest('.action-btn');
        const requestId = btn.dataset.requestId;
        const invitationId = btn.dataset.invitationId;
        const action = btn.dataset.action;

        if (action === 'accept') {
          this.acceptRequest(requestId, btn);
        } else if (action === 'decline') {
          this.declineRequest(requestId, btn);
        } else if (action === 'accept-invitation') {
          this.acceptInvitation(invitationId, btn);
        }
      }
    });

    // Listen for friend request events
    document.addEventListener('friend-request-sent', () => {
      // Refresh to show new sent request
      setTimeout(() => this.loadRequests(), 500);
    });

    // Listen for invitation events
    document.addEventListener('invitation-sent', () => {
      // Refresh to show new invitation or friend request
      setTimeout(() => this.loadRequests(), 500);
    });
  }

  async loadRequests() {
    this.isLoading = true;
    this.updateContent();

    try {
      const response = await window.API.request('/friends');
      this.receivedRequests = response.receivedRequests || [];
      this.sentRequests = response.sentRequests || [];
      this.pendingInvitations = response.pendingInvitations || [];

      // Dispatch event with request count for tab indicator
      this.dispatchEvent(new CustomEvent('friend-requests-updated', {
        detail: {
          receivedCount: this.receivedRequests.length,
          sentCount: this.sentRequests.length,
          pendingInvitationsCount: this.pendingInvitations.length
        },
        bubbles: true
      }));
    } catch (error) {
      window.logger.error('Failed to load friend requests:', error);
      this.receivedRequests = [];
      this.sentRequests = [];
      this.pendingInvitations = [];

      // Dispatch event with zero counts
      this.dispatchEvent(new CustomEvent('friend-requests-updated', {
        detail: {
          receivedCount: 0,
          sentCount: 0,
          pendingInvitationsCount: 0
        },
        bubbles: true
      }));
    } finally {
      this.isLoading = false;
      this.updateContent();
    }
  }

  async acceptRequest(requestId, btn) {
    const originalContent = btn.innerHTML;

    btn.disabled = true;
    btn.innerHTML = '<span>⏳</span> Accepting...';

    try {
      const response = await window.API.request(`/friends/accept/${requestId}`, {
        method: 'POST'
      });

      // Show success message
      this.showSuccess(response.message);

      // Refresh the requests (this will dispatch friend-requests-updated event)
      await this.loadRequests();

      // Dispatch event for other components
      this.dispatchEvent(new CustomEvent('friend-request-accepted', {
        detail: { requestId, friendship: response.friendship },
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Failed to accept friend request:', error);
      btn.disabled = false;
      btn.innerHTML = originalContent;
      this.showError(error.message || 'Failed to accept friend request');
    }
  }

  async declineRequest(requestId, btn) {
    const originalContent = btn.innerHTML;

    btn.disabled = true;
    btn.innerHTML = '<span>⏳</span> Declining...';

    try {
      await window.API.request(`/friends/decline/${requestId}`, {
        method: 'POST'
      });

      // Refresh the requests (this will dispatch friend-requests-updated event)
      await this.loadRequests();

      // Dispatch event for other components
      this.dispatchEvent(new CustomEvent('friend-request-declined', {
        detail: { requestId },
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Failed to decline friend request:', error);
      btn.disabled = false;
      btn.innerHTML = originalContent;
      this.showError(error.message || 'Failed to decline friend request');
    }
  }

  async acceptInvitation(invitationId, btn) {
    const originalContent = btn.innerHTML;

    btn.disabled = true;
    btn.innerHTML = '<span>⏳</span> Accepting...';

    try {
      const response = await window.API.request(`/friends/accept-invitation/${invitationId}`, {
        method: 'POST'
      });

      // Show success message
      this.showSuccess(response.message);

      // Refresh the requests
      await this.loadRequests();

      // Dispatch event for other components
      this.dispatchEvent(new CustomEvent('invitation-accepted', {
        detail: { invitationId, friendship: response.friendship },
        bubbles: true
      }));

    } catch (error) {
      window.logger.error('Failed to accept invitation:', error);
      btn.disabled = false;
      btn.innerHTML = originalContent;
      this.showError(error.message || 'Failed to accept invitation');
    }
  }

  updateContent() {
    this.render();
    const contentContainer = this.querySelector('.requests-content');
    if (contentContainer) {
      this.attachEventListeners();
    }
  }

  formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}d ago`;
    }
  }

  showSuccess(message) {
    const successDiv = document.createElement('div');
    successDiv.className = 'alert alert-success';
    successDiv.textContent = message;
    successDiv.style.position = 'fixed';
    successDiv.style.top = '20px';
    successDiv.style.right = '20px';
    successDiv.style.zIndex = '10000';
    successDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(successDiv);

    setTimeout(() => successDiv.remove(), 4000);
  }

  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'alert alert-error';
    errorDiv.textContent = message;
    errorDiv.style.position = 'fixed';
    errorDiv.style.top = '20px';
    errorDiv.style.right = '20px';
    errorDiv.style.zIndex = '10000';
    errorDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(errorDiv);

    setTimeout(() => errorDiv.remove(), 4000);
  }


  // Public method to refresh requests
  refresh() {
    this.loadRequests();
  }
}

// Register the custom element
customElements.define('friend-requests', FriendRequests);
